import { DynamicIcon  } from 'lucide-react/dynamic';
import { forwardRef, memo, useMemo } from "react";
import { useComposeClassNames } from "../hooks";
import type {IconName} from 'lucide-react/dynamic';
import type { ComponentProps} from "react";

export type { IconName };
export type IconSize = keyof typeof typography.icon;
export type IconVariant = "default" | "outline" | "solid" | "mini" | "duotone";

// Allow both strict IconName and general string for flexibility
type IconNameProp = IconName | (string & {});

export interface IconProps extends Omit<ComponentProps<typeof DynamicIcon>, 'color' | 'name'>{
  name: IconNameProp;
  size?: number | IconSize;
  iconVariant?: IconVariant; // Renamed from variant to iconVariant to avoid conflicts
  animated?: boolean;
  animationType?: "pulse" | "spin" | "bounce" | "wiggle";
  ariaLabel?: string;
  ariaHidden?: boolean;
  focusable?: boolean;
  disabled?: boolean;
}
const typography={
  icon: {
    xs: 'w-4 h-4',
    sm: 'w-5 h-5',
    base: 'w-6 h-6',
    lg: 'w-7 h-7',
    xl: 'w-8 h-8',
    '2xl': 'w-10 h-10'
  }
}

const IconComponent = forwardRef<SVGSVGElement, IconProps>(({ 
  size = "base",
  className,
  iconVariant = "default",
  disabled = false,    // Default disabled state
  animated = false,
  animationType = "spin",
  ariaLabel,
  ariaHidden = !ariaLabel,
  focusable = false,
  ...props 
}, ref) => {

  // Calculate actual size based on string or number
  const iconSize = useMemo(() => {
    if (typeof size === "number") {
      return size;
    }
    // Get the tailwind class from typography.icon
    return size in typography.icon ? undefined : 24;
  }, [size]);

  // Get color from theme if colorVariant is provided
  const iconColor = useMemo(() => {
    // If explicitly disabled, use gray color
    if (disabled) {
      return "#9CA3AF"; // gray-400
    }
    
    // Otherwise use the component's variant color
    return props.stroke || '';
  }, [ props.stroke, disabled]);

  // Define animation classes
  const animationClasses = useMemo(() => {
    if (!animated) return "";
    
    const animations = {
      pulse: "animate-pulse",
      spin: "animate-spin",
      bounce: "animate-bounce",
      wiggle: "animate-wiggle" // Requires custom animation in your tailwind config
    };
    
    return animations[animationType] || "";
  }, [animated, animationType]);

  // Compute size class
  const sizeClass = useMemo(() => {
    return typeof size === "string" && size in typography.icon ? typography.icon[size] : "";
  }, [size]);

  // Compose classes for the icon
  const iconClasses = useComposeClassNames({
    baseClasses: iconColor,
    additionalClasses: className,
    conditionalClasses: {
      [sizeClass]: !!sizeClass,
      "opacity-75": iconVariant === "outline",
      "stroke-1": iconVariant === "outline",
      "stroke-2": iconVariant === "default",
      "fill-current stroke-0": iconVariant === "solid",
      "stroke-[1.5px]": iconVariant === "mini",
      "fill-current stroke-[0.5px] opacity-90": iconVariant === "duotone",
      "opacity-60": disabled, // Add disabled state
      "transition-all duration-300": animated || true, // Always add transition for smoother UX
      "hover:scale-110": animated && !disabled,
      [animationClasses]: !!animationClasses && !disabled
    }
  });

  return (
    <DynamicIcon
      ref={ref}
      size={iconSize}
      className={iconClasses}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
      focusable={focusable}
      {...props}
      name={props.name as IconName}
    />
  );
});

IconComponent.displayName = "Icon";

export const IconRenderer = memo(IconComponent);
